import React, { useState } from "react";
import axios from "axios";
import { X, Mail, Lock, User, Leaf } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthModal({ onSuccess, onClose }) {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email, password } : { name, email, password };
      const res = await axios.post(`${API}${endpoint}`, payload);
      onSuccess(res.data.user, res.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = {
    backgroundColor: "#FDFBF7",
    border: "2px solid #E5E0D8",
    fontFamily: "Manrope, sans-serif",
    color: "#2D3436",
    width: "100%",
    padding: "12px 12px 12px 42px",
    borderRadius: "12px",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(45,52,54,0.5)", backdropFilter: "blur(6px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-md rounded-3xl p-8 relative overflow-hidden animate-fade-up"
        style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E0D8", boxShadow: "0 24px 64px rgba(0,0,0,0.14)" }}
      >
        {/* Top gradient accent */}
        <div className="absolute top-0 left-0 right-0 h-1 rounded-t-3xl"
          style={{ background: "linear-gradient(90deg, #E07A5F, #F2CC8F, #81B29A)" }} />

        {/* Close */}
        <button data-testid="auth-modal-close" onClick={onClose}
          className="absolute top-5 right-5 p-1.5 rounded-full transition-colors hover:bg-muted">
          <X size={16} style={{ color: "#9AA5B1" }} />
        </button>

        {/* Logo + Title */}
        <div className="flex items-center gap-2 mb-5">
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: "#E07A5F" }}>
            <Leaf size={13} color="white" strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-serif font-bold text-base leading-none" style={{ color: "#2D3436" }}>RasaBox</p>
            <p className="text-xs leading-none mt-0.5" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>by ThatAyurvedaGuy</p>
          </div>
        </div>

        <h2 className="font-serif font-bold text-2xl mb-1" style={{ color: "#2D3436" }}>
          {mode === "login" ? "Welcome back" : "Create your RasaBox"}
        </h2>
        <p className="text-sm mb-6" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>
          {mode === "login"
            ? "Sign in to access your personal Ayurvedic recipe book."
            : "Save your favourite recipes and build your personal recipe book."}
        </p>

        {/* Mode Toggle */}
        <div className="flex gap-1 p-1 rounded-xl mb-6" style={{ backgroundColor: "#F4F1EA" }}>
          {[["login", "Sign In"], ["register", "Join Free"]].map(([m, label]) => (
            <button key={m} data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(null); }}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all"
              style={{
                backgroundColor: mode === m ? "#FFFFFF" : "transparent",
                color: mode === m ? "#2D3436" : "#9AA5B1",
                boxShadow: mode === m ? "0 2px 8px rgba(0,0,0,0.07)" : "none",
                fontFamily: "Manrope, sans-serif",
              }}>
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "register" && (
            <div className="relative">
              <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#9AA5B1" }} />
              <input data-testid="auth-name-input" type="text" placeholder="Your name"
                value={name} onChange={e => setName(e.target.value)} required style={inputStyle} />
            </div>
          )}
          <div className="relative">
            <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#9AA5B1" }} />
            <input data-testid="auth-email-input" type="email" placeholder="Email address"
              value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} />
          </div>
          <div className="relative">
            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#9AA5B1" }} />
            <input data-testid="auth-password-input" type="password" placeholder="Password (min 6 chars)"
              value={password} onChange={e => setPassword(e.target.value)} required minLength={6} style={inputStyle} />
          </div>

          {error && (
            <div data-testid="auth-error" className="px-4 py-3 rounded-xl text-sm"
              style={{ backgroundColor: "#FEF2F2", color: "#C0392B", border: "1px solid #FECACA", fontFamily: "Manrope, sans-serif" }}>
              {error}
            </div>
          )}

          <button data-testid="auth-submit-btn" type="submit" disabled={isLoading}
            className="w-full py-3.5 rounded-xl font-semibold text-base transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: "#E07A5F", color: "#FFFFFF", fontFamily: "Manrope, sans-serif", boxShadow: "0 6px 20px rgba(224,122,95,0.3)" }}>
            {isLoading ? "Please wait..." : mode === "login" ? "Sign In" : "Create My RasaBox"}
          </button>
        </form>

        <p className="text-center text-xs mt-4" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>
          Your recipes stay private and safe.
        </p>
      </div>
    </div>
  );
}
